import React from 'react'
import { Table } from 'react-bootstrap'
import { useText } from '../context/TextContext'
import { DndContext, DragEndEvent, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { DND_ACTIVATION_DELAY_CONSTRAINT, DND_ACTIVATION_DISTANCE_CONSTRAINT } from '../utils/shared/constants'

export interface DataTableColumn<T> {
    label: string,
    key?: keyof T,
    render?: (item: T, ix: number) => JSX.Element | string
    skip?: (item: T) => boolean,
    align?: 'left' | 'right' | 'center',
}

export function DataTable<T>(props: {
    data: T[],
    columns: DataTableColumn<T>[],
    className?: string
    title?: string
    noHover?: boolean
    padding?: boolean
    onRowClick?: (row: T) => void
    footer?: (col: DataTableColumn<T>, ix: number) => React.ReactNode
    // Enables drag-and-drop row reordering; both are required for it.
    getRowId?: (row: T) => string
    onReorder?: (rows: T[]) => void
}) {
    const text = useText();
    const sensors = useSensors(
        useSensor(TouchSensor, { activationConstraint: DND_ACTIVATION_DELAY_CONSTRAINT }),
        useSensor(MouseSensor, { activationConstraint: DND_ACTIVATION_DISTANCE_CONSTRAINT }),
    );
    const { getRowId, onReorder } = props;
    const sortable = !!getRowId && !!onReorder;
    const skipColumnsIx: number[] = []
    for (let ix = 0; ix < props.columns.length; ix++) {
        const col = props.columns[ix]
        if (col.skip) {
            const skip = props.data.filter(col.skip)
            if (skip.length === props.data.length) {
                skipColumnsIx.push(ix)
            }
        }
    }
    const visibleColumns = props.columns.filter((col, ix) => !skipColumnsIx.includes(ix))
    const showHeader = !props.title;

    let wrapperClassName = `table-light w-100 overflow-auto`;
    if (props.padding) {
        wrapperClassName += ' p-3 rounded';
    }

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!getRowId || !onReorder || !over || active.id === over.id) return;

        const oldIndex = props.data.findIndex(row => getRowId(row) === active.id);
        const newIndex = props.data.findIndex(row => getRowId(row) === over.id);
        if (oldIndex < 0 || newIndex < 0) return;

        onReorder(arrayMove(props.data, oldIndex, newIndex));
    }

    const renderCells = (item: T, ix: number) => visibleColumns.map((col, colIx) => {
        let content: JSX.Element | string | null = null;
        if (col.render) {
            content = col.render(item, ix)
        } else if (!!col.key) {
            content = <>{item[col.key]}</>
        }

        return <td key={colIx}
            style={{ textAlign: col.align || 'left' }}
        >
            {content}
        </td>
    });

    const columnCount = visibleColumns.length + (sortable ? 1 : 0);

    const rows = props.data.map((item, ix) => getRowId && onReorder
        ? <SortableRow key={getRowId(item)} id={getRowId(item)} onClick={() => props.onRowClick && props.onRowClick(item)}>
            {renderCells(item, ix)}
        </SortableRow>
        : <tr key={ix} onClick={() => props.onRowClick && props.onRowClick(item)}>
            {renderCells(item, ix)}
        </tr>);

    return <div className={wrapperClassName} style={{ background: 'var(--bs-table-bg)' }}>
        {showHeader && <h2>{props.title}</h2>}
        <Table className={props.className} hover striped variant='light'>
            <thead>
                <tr>
                    {sortable && <th style={{ width: '1%' }}></th>}
                    {visibleColumns.map((col, ix) => <th key={ix}
                        style={{ textAlign: col.align || 'left' }}
                    >
                        {col.label}
                    </th>)}
                </tr>
            </thead>
            <tbody>
                {getRowId && onReorder
                    ? <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                        <SortableContext items={props.data.map(getRowId)} strategy={verticalListSortingStrategy}>
                            {rows}
                        </SortableContext>
                    </DndContext>
                    : rows}

                {props.data.length === 0 && <tr>
                    <td colSpan={columnCount} className='text-center p-3 text-muted small'>{text.noData}</td>
                </tr>}

            </tbody>
            {props.footer && props.data.length > 0 && <tfoot>
                <tr className='fw-semibold'>
                    {sortable && <td></td>}
                    {visibleColumns.map((col, ix) => <td key={ix}
                        style={{ textAlign: col.align || 'left' }}
                    >
                        {props.footer!(col, ix)}
                    </td>)}
                </tr>
            </tfoot>}
        </Table>
    </div>;

}

function SortableRow(props: { id: string, onClick: () => void, children: React.ReactNode }) {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: props.id });

    const style: React.CSSProperties = {
        transform: CSS.Translate.toString(transform),
        transition,
        position: 'relative',
        zIndex: isDragging ? 1 : undefined,
    };

    return <tr ref={setNodeRef} style={style} onClick={props.onClick}>
        <td className='text-secondary'>
            <span ref={setActivatorNodeRef} {...attributes} {...listeners} className='bi bi-grip-vertical' style={{ cursor: 'grab', touchAction: 'none' }}></span>
        </td>
        {props.children}
    </tr>;
}
